import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { createFileRoute } from "@tanstack/react-router";
import { Trash2, ToggleLeft, ToggleRight, BadgePercent } from "lucide-react";
import { useState, useEffect, useCallback } from "react";

import {
  Command,
  CommandInput,
  CommandList,
  CommandEmpty,
  CommandGroup,
  CommandItem,
} from "~/components/ui/command";
import { EmptyState } from "~/components/ui/empty-state";
import {
  Popover,
  PopoverContent,
  PopoverAnchor,
} from "~/components/ui/popover";
import { cn } from "~/lib/cn";
import { requirePermission } from "~/lib/functions/admin-access";
import {
  feeExemptionsQueryOptions,
  feeExemptionQueryKeys,
  createFeeExemptionFn,
  updateFeeExemptionFn,
  deleteFeeExemptionFn,
  searchCoachesFn,
  type CoachOption,
  type FeeExemptionRow,
} from "~/lib/functions/fee-exemption";

export const Route = createFileRoute("/admin/fee-exemptions/")({
  beforeLoad: ({ context }) =>
    requirePermission(context.adminPermissions, "coaches:read"),
  loader: async ({ context }) => {
    const exemptions = await context.queryClient.ensureQueryData(
      feeExemptionsQueryOptions,
    );
    return { exemptions };
  },
  component: FeeExemptionsAdminPage,
});

function getExemptionStatus(
  row: FeeExemptionRow,
): "active" | "expired" | "disabled" {
  if (!row.isActive) return "disabled";
  if (row.endDate && new Date(row.endDate) <= new Date()) return "expired";
  return "active";
}

function formatDate(date: Date | string | null): string {
  if (!date) return "-";
  const d = date instanceof Date ? date : new Date(date);
  if (Number.isNaN(d.getTime())) return "Invalid";
  return d.toLocaleDateString("en-US", {
    month: "short",
    day: "numeric",
    year: "numeric",
  });
}

function StatusBadge({
  status,
}: {
  status: "active" | "expired" | "disabled";
}) {
  return (
    <span
      className={cn(
        "inline-flex items-center rounded-full px-2.5 py-0.5 text-xs font-medium",
        status === "active" && "bg-emerald-100 text-emerald-700",
        status === "expired" && "bg-amber-100 text-amber-700",
        status === "disabled" && "bg-gray-100 text-gray-600",
      )}
    >
      {status === "active" && "Active"}
      {status === "expired" && "Expired"}
      {status === "disabled" && "Disabled"}
    </span>
  );
}

function CoachSearch({ onSelect }: { onSelect: (coach: CoachOption) => void }) {
  const [search, setSearch] = useState("");
  const [results, setResults] = useState<CoachOption[]>([]);
  const [isSearching, setIsSearching] = useState(false);
  const [open, setOpen] = useState(false);

  const doSearch = useCallback(async (term: string) => {
    if (term.length < 2) {
      setResults([]);
      setOpen(false);
      return;
    }
    setIsSearching(true);
    try {
      const coaches = await searchCoachesFn({
        data: { search: term },
      });
      setResults(coaches);
      setOpen(true);
    } finally {
      setIsSearching(false);
    }
  }, []);

  useEffect(() => {
    const timer = setTimeout(() => {
      doSearch(search);
    }, 300);
    return () => clearTimeout(timer);
  }, [search, doSearch]);

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverAnchor asChild>
        <Command shouldFilter={false} className="overflow-visible">
          <CommandInput
            value={search}
            onValueChange={setSearch}
            placeholder="Search by name or email..."
          />
        </Command>
      </PopoverAnchor>
      <PopoverContent
        className="w-[var(--radix-popover-trigger-width)] p-0"
        align="start"
        onOpenAutoFocus={(e) => e.preventDefault()}
      >
        <Command shouldFilter={false}>
          <CommandList>
            {isSearching ? (
              <div className="px-3 py-2 text-xs text-muted-foreground">
                Searching...
              </div>
            ) : results.length === 0 ? (
              <CommandEmpty>No coaches found.</CommandEmpty>
            ) : (
              <CommandGroup>
                {results.map((coach) => (
                  <CommandItem
                    key={coach.id}
                    value={coach.id}
                    onSelect={() => {
                      onSelect(coach);
                      setSearch(coach.fullName);
                      setOpen(false);
                    }}
                  >
                    <div>
                      <div className="font-medium text-gray-900">
                        {coach.fullName}
                      </div>
                      <div className="text-xs text-gray-500">{coach.email}</div>
                    </div>
                  </CommandItem>
                ))}
              </CommandGroup>
            )}
          </CommandList>
        </Command>
      </PopoverContent>
    </Popover>
  );
}

function FeeExemptionsAdminPage() {
  const queryClient = useQueryClient();

  const { data: exemptions, isLoading } = useQuery(feeExemptionsQueryOptions);

  const [selectedCoach, setSelectedCoach] = useState<CoachOption | null>(null);
  const [reason, setReason] = useState("");
  const [endDate, setEndDate] = useState("");
  const [successMessage, setSuccessMessage] = useState("");

  const showSuccess = (msg: string) => {
    setSuccessMessage(msg);
    setTimeout(() => setSuccessMessage(""), 3000);
  };

  const createMutation = useMutation({
    mutationFn: createFeeExemptionFn,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: feeExemptionQueryKeys.all });
      setSelectedCoach(null);
      setReason("");
      setEndDate("");
      showSuccess("Fee exemption created successfully.");
    },
  });

  const toggleMutation = useMutation({
    mutationFn: updateFeeExemptionFn,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: feeExemptionQueryKeys.all });
      showSuccess("Exemption updated.");
    },
  });

  const deleteMutation = useMutation({
    mutationFn: deleteFeeExemptionFn,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: feeExemptionQueryKeys.all });
      showSuccess("Exemption deleted.");
    },
  });

  const handleCreate = (e: React.FormEvent) => {
    e.preventDefault();
    if (!selectedCoach || !reason.trim()) return;
    createMutation.mutate({
      data: {
        coachId: selectedCoach.id,
        reason: reason.trim(),
        endDate: endDate ? new Date(endDate) : null,
      },
    });
  };

  const handleToggle = (row: FeeExemptionRow) => {
    toggleMutation.mutate({
      data: {
        exemptionId: row.id,
        isActive: !row.isActive,
      },
    });
  };

  const handleDelete = (row: FeeExemptionRow) => {
    deleteMutation.mutate({
      data: {
        exemptionId: row.id,
      },
    });
  };

  const activeCount =
    exemptions?.filter((e) => getExemptionStatus(e) === "active").length ?? 0;

  if (isLoading) {
    return (
      <div className="flex min-h-screen items-center justify-center bg-gray-50">
        <div className="text-gray-600">Loading...</div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50 p-6">
      <div className="mx-auto max-w-4xl">
        <h1 className="mb-2 text-2xl font-bold text-gray-900">
          Coach Fee Exemptions
        </h1>
        <p className="mb-6 text-sm text-gray-500">
          Exempt coaches from platform fees. Stripe processing fees still apply.
        </p>

        {/* Success Banner */}
        {successMessage && (
          <div className="mb-4 rounded-lg border border-emerald-200 bg-emerald-50 px-4 py-3 text-sm text-emerald-700">
            {successMessage}
          </div>
        )}

        {/* Error Banner */}
        {(createMutation.error ||
          toggleMutation.error ||
          deleteMutation.error) && (
          <div className="mb-4 rounded-lg border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-700">
            An error occurred. Please try again.
          </div>
        )}

        {/* Summary */}
        <div className="mb-6 rounded-xl border border-gray-200 bg-white p-4 shadow-sm">
          <div className="flex items-center gap-4">
            <div>
              <div className="text-2xl font-bold text-gray-900">
                {activeCount}
              </div>
              <div className="text-xs text-gray-500">
                Active exemption{activeCount !== 1 ? "s" : ""}
              </div>
            </div>
            <div>
              <div className="text-2xl font-bold text-gray-900">
                {exemptions?.length ?? 0}
              </div>
              <div className="text-xs text-gray-500">Total records</div>
            </div>
          </div>
        </div>

        {/* Add Exemption Form */}
        <div className="mb-6 rounded-xl border border-gray-200 bg-white p-6 shadow-sm">
          <h2 className="mb-4 text-sm font-semibold tracking-wide text-gray-500 uppercase">
            Add New Exemption
          </h2>
          <form onSubmit={handleCreate} className="space-y-4">
            <div>
              <label className="mb-1 block text-sm font-medium text-gray-700">
                Coach
              </label>
              <CoachSearch onSelect={(coach) => setSelectedCoach(coach)} />
              {selectedCoach && (
                <div className="mt-1 text-xs text-emerald-600">
                  Selected: {selectedCoach.fullName} ({selectedCoach.email})
                </div>
              )}
            </div>

            <div>
              <label
                htmlFor="reason"
                className="mb-1 block text-sm font-medium text-gray-700"
              >
                Reason
              </label>
              <input
                id="reason"
                type="text"
                value={reason}
                onChange={(e) => setReason(e.target.value)}
                placeholder="e.g., Beta tester for new features"
                className="w-full rounded-lg border border-gray-200 px-3 py-2 text-sm shadow-sm focus:border-brand-500 focus:ring-2 focus:ring-brand-100 focus:outline-none"
              />
            </div>

            <div>
              <label
                htmlFor="endDate"
                className="mb-1 block text-sm font-medium text-gray-700"
              >
                End Date{" "}
                <span className="font-normal text-gray-400">(optional)</span>
              </label>
              <input
                id="endDate"
                type="date"
                value={endDate}
                onChange={(e) => setEndDate(e.target.value)}
                className="w-full rounded-lg border border-gray-200 px-3 py-2 text-sm shadow-sm focus:border-brand-500 focus:ring-2 focus:ring-brand-100 focus:outline-none"
              />
              <p className="mt-1 text-xs text-gray-500">
                Leave blank for indefinite exemption. You can always disable it
                manually.
              </p>
            </div>

            <button
              type="submit"
              disabled={
                !selectedCoach || !reason.trim() || createMutation.isPending
              }
              className="rounded-lg bg-brand-600 px-5 py-2 text-sm font-semibold text-white shadow-sm transition hover:bg-brand-700 disabled:cursor-not-allowed disabled:opacity-50"
            >
              {createMutation.isPending ? "Creating..." : "Create Exemption"}
            </button>
          </form>
        </div>

        {/* Exemptions Table */}
        <div className="rounded-xl border border-gray-200 bg-white shadow-sm">
          <h2 className="border-b px-6 py-4 text-sm font-semibold tracking-wide text-gray-500 uppercase">
            All Exemptions
          </h2>
          {!exemptions?.length ? (
            <EmptyState
              size="sm"
              icon={BadgePercent}
              title="No fee exemptions"
              description="Add an exemption above to waive platform fees for a coach."
            />
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="border-b bg-gray-50 text-left text-xs tracking-wider text-gray-500 uppercase">
                    <th className="px-4 py-3">Coach</th>
                    <th className="px-4 py-3">Reason</th>
                    <th className="px-4 py-3">Start</th>
                    <th className="px-4 py-3">End</th>
                    <th className="px-4 py-3">Status</th>
                    <th className="px-4 py-3 text-right">Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {exemptions.map((row) => {
                    const status = getExemptionStatus(row);
                    return (
                      <tr
                        key={row.id}
                        className="border-b last:border-0 hover:bg-gray-50"
                      >
                        <td className="px-4 py-3">
                          <div className="font-medium text-gray-900">
                            {row.coachName}
                          </div>
                          <div className="text-xs text-gray-500">
                            {row.coachEmail}
                          </div>
                        </td>
                        <td className="px-4 py-3 text-gray-700">
                          {row.reason}
                        </td>
                        <td className="px-4 py-3 whitespace-nowrap text-gray-600">
                          {formatDate(row.startDate)}
                        </td>
                        <td className="px-4 py-3 whitespace-nowrap text-gray-600">
                          {formatDate(row.endDate)}
                        </td>
                        <td className="px-4 py-3">
                          <StatusBadge status={status} />
                        </td>
                        <td className="px-4 py-3 text-right">
                          <div className="flex items-center justify-end gap-2">
                            <button
                              type="button"
                              title={
                                row.isActive
                                  ? "Disable exemption"
                                  : "Enable exemption"
                              }
                              onClick={() => handleToggle(row)}
                              disabled={toggleMutation.isPending}
                              className={cn(
                                "rounded p-1.5 transition",
                                row.isActive
                                  ? "text-emerald-600 hover:bg-emerald-50"
                                  : "text-gray-400 hover:bg-gray-100",
                              )}
                            >
                              {row.isActive ? (
                                <ToggleRight className="h-5 w-5" />
                              ) : (
                                <ToggleLeft className="h-5 w-5" />
                              )}
                            </button>
                            <button
                              type="button"
                              title="Delete exemption"
                              onClick={() => handleDelete(row)}
                              disabled={deleteMutation.isPending}
                              className="rounded p-1.5 text-red-500 transition hover:bg-red-50"
                            >
                              <Trash2 className="h-4 w-4" />
                            </button>
                          </div>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
